"use client";

import { useRef, useState, useEffect } from "react";
import Image from "next/image";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { GBP_URL, GBP_RATING, GBP_REVIEW_COUNT } from "@/lib/contact";

// `logo` and `photo` are optional; cards fall back to initials when missing.
type Testimonial = {
  quote: string;
  name: string;
  role: string;
  company: string;
  logo?: string;
  photo?: string;
};

const testimonials: Testimonial[] = [
  {
    quote:
      "Lead Workz manages our Meta ad campaigns, and the results speak for themselves — a 6.8× return on our ad spend. They're easy to work with and clearly understand how to turn ad budget into actual bookings, not just clicks.",
    name: "Shikha",
    role: "Owner",
    company: "Design1Studio",
    logo: "/images/our-work/d1s-logo.webp",
  },
  {
    quote:
      "Lead Workz runs our Meta campaigns across all our treatments, and the enquiry volume has been consistent month after month. Their team is quick to react when a campaign needs adjusting.",
    name: "Sudeer",
    role: "Founder",
    company: "KOZA",
    logo: "/images/our-work/koza-logo-gold.webp",
  },
  {
    quote:
      "Lead Workz's Meta campaigns bring us direct enquiries at just ₹14 per result, and about 1 in 5 of those turn into a confirmed booking — it's made our booking pipeline far more predictable than it used to be.",
    name: "Faizal",
    role: "Owner",
    company: "Aaramgram",
  },
  {
    quote:
      "Lead Workz built our new website and it finally reflects the scale of our operations. Beyond the website, they manage our email marketing too, so our communication with clients and prospects stays consistent.",
    name: "Ankur Goyal",
    role: "Director",
    company: "Digital Circuits",
  },
];

export default function Testimonials() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  function scrollToIndex(i: number) {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const card = scroller.children[i] as HTMLElement | undefined;
    if (card) {
      scroller.scrollTo({ left: card.offsetLeft - 24, behavior: "smooth" });
    }
  }

  function prev() {
    scrollToIndex(Math.max(0, active - 1));
  }
  function next() {
    scrollToIndex(Math.min(testimonials.length - 1, active + 1));
  }

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    function onScroll() {
      if (!scroller) return;
      const children = Array.from(scroller.children) as HTMLElement[];
      let closest = 0;
      let closestDist = Infinity;
      children.forEach((child, i) => {
        const dist = Math.abs(child.offsetLeft - 24 - scroller.scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      setActive(closest);
    }

    function updateCanScroll() {
      if (!scroller) return;
      setCanScroll(scroller.scrollWidth > scroller.clientWidth + 1);
    }

    updateCanScroll();
    scroller.addEventListener("scroll", onScroll, { passive: true });

    const resizeObserver = new ResizeObserver(updateCanScroll);
    resizeObserver.observe(scroller);

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <section id="testimonials" className="w-full overflow-hidden bg-black py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-heading text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl">
            What our <span className="text-[#ceff1a]">clients</span> say
          </h2>
          <a
            href={GBP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mr-auto inline-flex shrink-0 items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-[#ceff1a]"
          >
            <Star size={16} className="fill-[#ceff1a] text-[#ceff1a]" />
            {GBP_RATING} on Google
            <span className="text-white/50">· {GBP_REVIEW_COUNT} reviews</span>
          </a>
          {canScroll && (
            <div className="hidden shrink-0 items-center gap-3 sm:flex">
              <button
                type="button"
                onClick={prev}
                disabled={active === 0}
                aria-label="Previous testimonial"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#ceff1a] hover:text-[#ceff1a] disabled:pointer-events-none disabled:opacity-30"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={next}
                disabled={active === testimonials.length - 1}
                aria-label="Next testimonial"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#ceff1a] hover:text-[#ceff1a] disabled:pointer-events-none disabled:opacity-30"
              >
                <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>

        <div
          ref={scrollerRef}
          className="mt-14 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {testimonials.map((t) => (
            <div
              key={t.name}
              className="w-[320px] shrink-0 snap-start rounded-2xl border border-white/10 bg-white/[0.03] p-8 sm:w-[380px]"
            >
              <div className="flex h-14 items-center gap-3">
                {t.logo ? (
                  <span className="flex h-14 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
                    <Image src={t.logo} alt={`${t.company} logo`} width={224} height={96} className="h-full w-full object-contain" />
                  </span>
                ) : (
                  <>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1d4ed8] to-[#0047ab] font-heading text-sm font-extrabold text-white">
                      {t.company.charAt(0)}
                    </span>
                    <span className="font-heading text-base font-extrabold tracking-wide text-white uppercase">
                      {t.company}
                    </span>
                  </>
                )}
              </div>

              <p className="mt-6 text-[15px] leading-7 text-white/70">
                &ldquo;{t.quote}&rdquo;
              </p>

              <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-6">
                {t.photo ? (
                  <Image src={t.photo} alt={t.name} width={40} height={40} className="h-10 w-10 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 font-heading text-sm font-bold text-white">
                    {t.name.charAt(0)}
                  </span>
                )}
                <div>
                  <div className="text-sm font-bold text-white">
                    {t.name}
                  </div>
                  <div className="text-xs text-white/60">
                    {t.role}, {t.company}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="w-px shrink-0" aria-hidden="true" />
        </div>
      </div>

      {canScroll && (
        <div className="mt-8 flex items-center justify-center gap-2">
          {testimonials.map((t, i) => (
            <button
              key={t.name}
              type="button"
              onClick={() => scrollToIndex(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className="flex h-6 w-6 items-center justify-center"
            >
              <span
                className={
                  i === active
                    ? "h-2.5 w-2.5 rounded-full bg-[#ceff1a] transition-colors"
                    : "h-2.5 w-2.5 rounded-full bg-white/20 transition-colors hover:bg-white/40"
                }
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
